"use client";

import { useEffect, useRef } from "react";

const STORAGE_KEY = "granbay.orderbookCollapsed";

/**
 * 2026-04-30 — wraps the order-book section so the open/closed state
 * survives page reloads via localStorage.
 *
 * Default posture: closed (matches PR #472's Polymarket-parity default).
 * On mount we read the saved preference and apply it; on toggle we
 * write the new value back. The native <details> element handles
 * keyboard / accessibility / SSR — we just persist the boolean.
 *
 * Server-rendered HTML stays default-closed (no `open` attribute) so
 * the first paint matches the rule the founder set on 2026-04-29.
 * If the user previously expanded it, the client-side effect bumps
 * `details.open = true` shortly after hydration.
 */
export function CollapsibleOrderbook({
  summary,
  children,
}: {
  summary: React.ReactNode;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    try {
      // "1" = expanded, anything else = closed (default).
      if (localStorage.getItem(STORAGE_KEY) === "1") {
        el.open = true;
      }
    } catch {
      // Storage blocked (private mode, quota, etc.) — silently
      // fall back to the SSR default.
    }
    const onToggle = () => {
      try {
        localStorage.setItem(STORAGE_KEY, el.open ? "1" : "0");
      } catch {
        // ignore — storage write blocked
      }
    };
    el.addEventListener("toggle", onToggle);
    return () => el.removeEventListener("toggle", onToggle);
  }, []);

  return (
    <details ref={ref} className="mb-6 group">
      <summary className="mb-2 flex min-h-[40px] cursor-pointer list-none items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-[var(--color-text3)] hover:text-[var(--color-text2)] md:min-h-0">
        <span className="select-none transition-transform group-open:rotate-90">
          ▶
        </span>
        {summary}
      </summary>
      {children}
    </details>
  );
}
